
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { getBrowserLang, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { Observable, of, startWith } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { CategoryElement } from '@core/data/partial/categories.model';
import { AccountApiService } from '@core/services/api/account-api.service';
import { APIService } from '@core/services/api/api.service';
import { SupportApiService } from '@core/services/api/support-api.service';
import { UserProfileService } from '@core/services/user-profile.service';
import { selectAddTranslationKeysLanguage } from '@core/store/selectors/app-config.selectors';
import { tokenSelector } from '@core/store/selectors/session.selectors';
import { userProfileSelector } from '@core/store/selectors/user-profile.selectors';
import {
  AVAILABLE_LANGUAGES,
  FALLBACK_LANGUAGE,
  KEYS_LANGUAGE,
} from '../../transloco/transloco-root.module';
import { LanguageProvider } from '@xt/data-access';

export type LocaleInfo = {
  locale: string;
  flag?: string;
  display?: string;
};

@Injectable({
  providedIn: 'root',
})
export class LocaleService implements LanguageProvider {
  availableLanguages$: Observable<CategoryElement[]>;

  currentLang$ = this.translocoService.langChanges$.pipe(
    startWith(this.translocoService.getDefaultLang()),
  );
  getCurrentLanguage = () => this.translocoService.getActiveLang();

  constructor(
    private translocoService: TranslocoService,
    private apiService: APIService,
    private store: Store,
    private userProfileService: UserProfileService,
    private accountApiService: AccountApiService,
    private supportApiService: SupportApiService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // process locale change
    this.translocoService.langChanges$.subscribe((lang) =>
      this.onLocaleChange(lang),
    );

    // set user locale by login
    this.store
      .select(tokenSelector)
      .pipe(
        filter((token) => !!token),
        switchMap((token) => this.accountApiService.getUserLocale$()),
      )
      .subscribe(({ locale }) => {
        if (this.translocoService.getActiveLang() !== locale) {
          this.translocoService.setActiveLang(locale);
        }
      });
  }

  // method returns Promise to use in APP_INITIALIZER provider
  init(): Promise<any> {
    const supportedLocalesInfo$ = this.supportApiService
      .getSupportedLocales$()
      .pipe(
        catchError(() =>
          of({
            defaultLocale: null,
            supportedLocales: [],
          }),
        ),
        tap(({ supportedLocales }) =>
          supportedLocales.forEach(({ locale }) => {
            if (!AVAILABLE_LANGUAGES.includes(locale))
              AVAILABLE_LANGUAGES.push(locale);
          }),
        ),
      );

    // init available locales list
    this.availableLanguages$ = supportedLocalesInfo$.pipe(
      map(({ supportedLocales }) =>
        supportedLocales.map(({ locale, display }) => ({
          id: locale,
          name: display,
        })),
      ),
      switchMap((languages) =>
        this.store.pipe(
          selectAddTranslationKeysLanguage,
          map((addTranslationKeys) =>
            addTranslationKeys
              ? [...languages, { id: KEYS_LANGUAGE, name: 'Translation' }]
              : languages,
          ),
        ),
      ),
      shareReplay(1),
    );

    // init active locale
    const activeLocale$ = supportedLocalesInfo$.pipe(
      map(({ defaultLocale, supportedLocales }) => {
        const browserLang = getBrowserLang();
        const browserLocale = supportedLocales.find(({ locale }) =>
          locale.startsWith(browserLang),
        )?.locale;

        this.translocoService.setDefaultLang(
          browserLocale ||
            defaultLocale ||
            this.translocoService.getDefaultLang(),
        );

        return this.translocoService.getDefaultLang();
      }),
      switchMap((defaultLocale) =>
        this.accountApiService.getUserLocale$().pipe(
          map(({ locale }) => locale),
          catchError(() => of(defaultLocale)),
        ),
      ),
      take(1),
    );

    return activeLocale$
      .pipe(
        switchMap((activeLocale) => {
          // set active language
          this.translocoService.setActiveLang(activeLocale);

          // load active language
          return this.translocoService.load(activeLocale);
        }),
      )
      .toPromise();
  }

  private onLocaleChange(locale) {
    if (this.translocoService.isLang(locale)) {
      if (this.document) this.document.documentElement.lang = locale;

      this.translocoService.setFallbackLangForMissingTranslation({
        fallbackLang:
          locale === KEYS_LANGUAGE ? KEYS_LANGUAGE : FALLBACK_LANGUAGE,
      });

      this.store
        .select(tokenSelector)
        .pipe(take(1))
        .subscribe((token) => {
          if (token) {
            this.accountApiService.setUserLocale$(locale).subscribe(() => {
              // synchronize saved locale with userProfile
              this.store
                .select(userProfileSelector)
                .pipe(take(1))
                .subscribe((userProfile) => {
                  if (userProfile) {
                    this.userProfileService
                      .updateUserProfile$({ language: locale })
                      .subscribe();
                  }
                });
            });
          }
        });
    }
  }
}
